"use client";

import {
  Archive,
  CircleAlert,
  CircleCheck,
  CircleX,
  Info,
  TriangleAlert,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  forwardRef,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type ReactNode,
} from "react";

import { domId } from "@/lib/dom-id";
import { cn } from "@/lib/utils";

export type StatusToastStatus = "error" | "info" | "neutral" | "success" | "warning";
export type StatusToastSize = "medium" | "small";

export interface StatusToastAction {
  label: ReactNode;
  onClick: ButtonHTMLAttributes<HTMLButtonElement>["onClick"];
}

export interface StatusToastProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  /**
   * Base of the data-testid values (`div-<purpose>`, `btn-<purpose>-dismiss`).
   * A `data-testid` passed in props replaces the root's.
   */
  purpose: string;
  action?: StatusToastAction;
  children?: ReactNode;
  dismissLabel?: string;
  onDismiss?: () => void;
  size?: StatusToastSize;
  status?: StatusToastStatus;
  title?: ReactNode;
}

const toneStyles: Record<
  StatusToastStatus,
  {
    action: string;
    border: string;
    foreground: string;
    icon: string;
    surface: string;
  }
> = {
  error: {
    action: "bg-[#DC2626] hover:bg-[#991B1B] focus-visible:ring-[#DC2626]",
    border: "border-[#FECACA]",
    foreground: "text-[#991B1B]",
    icon: "text-[#DC2626]",
    surface: "bg-[#FEF2F2]",
  },
  info: {
    action: "bg-[#014EA9] hover:bg-[#025ECA] focus-visible:ring-[#014EA9]",
    border: "border-[#014EA9]",
    foreground: "text-[#014EA9]",
    icon: "text-[#025ECA]",
    surface: "bg-[#EBF5FF]",
  },
  neutral: {
    action: "bg-[#6B7280] hover:bg-[#374151] focus-visible:ring-[#6B7280]",
    border: "border-[#E5E7EB]",
    foreground: "text-[#374151]",
    icon: "text-[#6B7280]",
    surface: "bg-[#F9FAFB]",
  },
  success: {
    action: "bg-[#16A34A] hover:bg-[#166534] focus-visible:ring-[#16A34A]",
    border: "border-[#BBF7D0]",
    foreground: "text-[#166534]",
    icon: "text-[#16A34A]",
    surface: "bg-[#F0FDF4]",
  },
  warning: {
    action: "bg-[#CA8A04] hover:bg-[#854D0E] focus-visible:ring-[#CA8A04]",
    border: "border-[#FEF08A]",
    foreground: "text-[#854D0E]",
    icon: "text-[#CA8A04]",
    surface: "bg-[#FEFCE8]",
  },
};

const statusIcons: Record<StatusToastStatus, LucideIcon> = {
  error: CircleX,
  info: Info,
  neutral: Archive,
  success: CircleCheck,
  warning: CircleAlert,
};

/**
 * SilverTiger status toast, ported from frontend-internal-admin
 * (src/components/ui/toast.tsx), where it also serves as the inline error of
 * the Wiki upload form.
 *
 * Without a title it renders the compact Figma variant. Supplying a title
 * automatically switches to the expanded alert layout with an accent border.
 */
export const StatusToast = forwardRef<HTMLDivElement, StatusToastProps>(function StatusToast(
  {
    purpose,
    action,
    children,
    className,
    dismissLabel = "Dismiss notification",
    onDismiss,
    role,
    size = "small",
    status = "success",
    title,
    ...props
  },
  ref,
) {
  const hasTitle = title !== undefined && title !== null && title !== "";
  const tone = toneStyles[status];
  const StatusIcon = hasTitle && status === "warning"
    ? TriangleAlert
    : statusIcons[status];

  return (
    <div
      data-testid={domId("div", purpose)}
      {...props}
      className={cn(
        "relative flex min-w-0 rounded-lg",
        tone.surface,
        tone.foreground,
        hasTitle
          ? "w-full max-w-[520px] items-start gap-2 border-l-[3px] px-4 py-3"
          : cn(
              "w-full max-w-[395px] items-center p-2",
              size === "small" ? "gap-1" : "gap-2",
            ),
        hasTitle && tone.border,
        className,
      )}
      ref={ref}
      role={role ?? (status === "error" ? "alert" : "status")}
    >
      <span
        className={cn(
          "grid shrink-0 place-items-center",
          tone.icon,
          hasTitle ? "size-[18px]" : "size-5",
        )}
      >
        <StatusIcon
          aria-hidden="true"
          size={hasTitle ? 18 : 16}
          strokeWidth={2}
        />
      </span>

      {hasTitle ? (
        <div className="min-w-0 flex-1 break-words leading-[1.4]">
          <p className="text-sm font-semibold">{title}</p>
          {children ? (
            <div className="mt-0.5 text-[13px] font-normal">{children}</div>
          ) : null}
        </div>
      ) : (
        <div
          className={cn(
            "min-w-0 flex-1 break-words font-medium leading-[1.4]",
            size === "small" ? "text-xs" : "text-sm",
          )}
        >
          {children}
        </div>
      )}

      {action && hasTitle ? (
        <button
          className={cn(
            "inline-flex h-8 shrink-0 items-center justify-center rounded-lg px-4 text-[13px] font-medium text-white transition-colors",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
            tone.action,
          )}
          data-testid={domId("btn", `${purpose}-action`)}
          onClick={action.onClick}
          type="button"
        >
          {action.label}
        </button>
      ) : null}

      {onDismiss ? (
        <button
          aria-label={dismissLabel}
          data-testid={domId("btn", `${purpose}-dismiss`)}
          className="grid size-5 shrink-0 place-items-center rounded text-current transition-colors hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current focus-visible:ring-offset-1"
          onClick={onDismiss}
          type="button"
        >
          <X aria-hidden="true" size={16} strokeWidth={2} />
        </button>
      ) : null}
    </div>
  );
});

StatusToast.displayName = "StatusToast";
